/**
 * The tasks list's open first tasks (Feature 71, D33): which rows are
 * opened, their linked tasks fetched by `parent_task_id` while open, and
 * the rows the table draws with those linked tasks beneath them. A first
 * task stays open while the list polls, and its linked tasks poll with it.
 */
import { useQueries } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { listTasks } from '@optum/api-client/endpoints/tasks';
import type { Task } from '@optum/api-client/types';

import { qk } from '../../lib/query-keys.js';
import { oldestFirst, withLinked, type NestedRows } from './linked-rows.js';

// A template has at most twenty steps; a first task runs every enabled
// template, so one page of this size holds any first task's linked tasks.
const LINKED_LIMIT = 200;

export interface LinkedRows extends NestedRows {
  isOpen: (taskId: string) => boolean;
  toggle: (taskId: string) => void;
}

export function useLinkedRows(rows: readonly Task[], refetchInterval: number | false): LinkedRows {
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  // Every opened task, a first task or an independent one beneath it (D59).
  const ids = [...open];

  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: qk.tasks({ parent_task_id: id }),
      queryFn: () => listTasks({ parent_task_id: id, limit: LINKED_LIMIT }),
      refetchInterval,
    })),
  });

  const toggle = useCallback((taskId: string) => {
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(taskId)) next.add(taskId);
      return next;
    });
  }, []);
  const isOpen = useCallback((taskId: string) => open.has(taskId), [open]);

  const linked = new Map(ids.map((id, i) => [id, oldestFirst(queries[i]?.data ?? [])]));
  return { ...withLinked(rows, linked), isOpen, toggle };
}
